import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, Share2, ChevronDown, Clock, User, Monitor, Wifi, Maximize2, Play, Info, CheckCircle, Globe, Flame, TreePine } from 'lucide-react';

// --- CONFIGURATION (UTC TIMES) ---
// Season 4 Premiere: April 19, 2026
// Mid-Season: May 17, 2026
// Finale: June 21, 2026
const RELEASE_CONFIG = {
  premiere: new Date('2026-04-19T01:00:00Z').getTime(),
  midseason: new Date('2026-05-17T01:00:00Z').getTime(),
  finale: new Date('2026-06-21T01:00:00Z').getTime()
};

// --- HOOK: COUNTDOWN LOGIC ---
const useCountdown = (targetDate) => {
  const [now, setNow] = useState(new Date().getTime());
  
  useEffect(() => {
    if (!targetDate) return;
    const interval = setInterval(() => {
      setNow(new Date().getTime());
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  if (!targetDate) return { days: 0, hours: 0, minutes: 0, seconds: 0, isReleased: true };

  const countDown = targetDate - now;
  if (countDown <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, isReleased: true };

  const days = Math.floor(countDown / (1000 * 60 * 60 * 24));
  const hours = Math.floor((countDown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((countDown % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((countDown % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, isReleased: false };
};

// --- GLOBAL STYLES & ANIMATIONS ---
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@700&family=Bebas+Neue&family=Inter:wght@300;400;600;800&display=swap');

    :root {
      --mgm-gold: #cda85f;
      --horror-red: #8b0000;
      --bg-black: #030405;
      --text-gray: #b3b3b3;
    }

    body {
      background-color: var(--bg-black);
      color: #e5e5e5;
      font-family: 'Inter', sans-serif;
      overflow-x: hidden;
    }

    .film-grain {
      position: fixed;
      top: 0; left: 0; width: 100%; height: 100%;
      pointer-events: none;
      z-index: 9999;
      opacity: 0.05;
      background-image: url("https://www.transparenttextures.com/patterns/stardust.png");
      animation: grain 8s steps(10) infinite;
    }

    .font-title {
      font-family: 'Libre Baskerville', serif;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: #fff;
      text-shadow: 0 0 20px rgba(255, 255, 255, 0.3), 3px 3px 0px #000;
      -webkit-text-stroke: 1px rgba(0,0,0,0.8);
    }

    .font-display {
      font-family: 'Bebas Neue', sans-serif;
      letter-spacing: 0.1em;
    }

    @keyframes grain {
      0%, 100% { transform: translate(0, 0); }
      10% { transform: translate(-5%, -5%); }
      20% { transform: translate(-10%, 5%); }
      30% { transform: translate(5%, -10%); }
      40% { transform: translate(-5%, 15%); }
      50% { transform: translate(-10%, 5%); }
      60% { transform: translate(15%, 0); }
      70% { transform: translate(0, 10%); }
      80% { transform: translate(3%, 35%); }
      90% { transform: translate(-10%, 10%); }
    }

    @keyframes slow-zoom {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }

    @keyframes fade-in-up {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .animate-slow-zoom { animation: slow-zoom 25s ease-in-out infinite alternate; }
    .animate-fade-up { animation: fade-in-up 1s ease-out forwards; }

    ::-webkit-scrollbar { width: 8px; }
    ::-webkit-scrollbar-track { background: #000; }
    ::-webkit-scrollbar-thumb { background: #222; border-radius: 4px; }
    ::-webkit-scrollbar-thumb:hover { background: var(--mgm-gold); }
  `}</style>
);

// --- Component: Particle System (Forest Spores/Dust) ---
const DustParticles = React.memo(() => {
  const particles = useMemo(() => [...Array(30)].map((_, i) => ({
    id: i,
    size: Math.random() * 3 + 1,
    top: Math.random() * 100,
    left: Math.random() * 100,
    duration: Math.random() * 20 + 15,
    delay: -(Math.random() * 15)
  })), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-20">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute bg-[#cda85f]/20 rounded-full"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            top: `${p.top}%`,
            left: `${p.left}%`,
            animation: `drift ${p.duration}s linear infinite`,
            animationDelay: `${p.delay}s`
          }}
        />
      ))}
      <style>{`
        @keyframes drift {
          0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0; }
          20% { opacity: 0.5; }
          80% { opacity: 0.5; }
          100% { transform: translateY(-50vh) translateX(20vw) rotate(360deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
});

// --- Component: Countdown Timer ---
const CountdownDisplay = ({ targetDate, title }) => {
  const { days, hours, minutes, seconds, isReleased } = useCountdown(targetDate);

  if (isReleased) return null;

  const TimeUnit = ({ value, label }) => (
    <div className="flex flex-col items-center mx-2 md:mx-4">
      <div className="bg-black/60 border border-white/5 backdrop-blur-sm w-16 md:w-24 h-20 md:h-28 flex items-center justify-center rounded-lg shadow-2xl relative overflow-hidden group">
        <div className="absolute inset-0 bg-[#cda85f]/5 group-hover:bg-[#cda85f]/10 transition-colors"></div>
        <span className="font-display text-4xl md:text-6xl text-white relative z-10">
          {value < 10 ? `0${value}` : value}
        </span>
      </div>
      <span className="text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-[#cda85f] mt-3">{label}</span>
    </div>
  );

  return (
    <div className="mb-12 animate-fade-up w-full">
      <div className="text-center mb-6">
          <span className="bg-[#cda85f]/10 text-[#cda85f] text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest border border-[#cda85f]/30 inline-flex items-center gap-2">
            <Globe size={12} /> {title}
          </span>
      </div>
      <div className="flex justify-center flex-wrap gap-y-4">
        <TimeUnit value={days} label="Days" />
        <TimeUnit value={hours} label="Hours" />
        <TimeUnit value={minutes} label="Mins" />
        <TimeUnit value={seconds} label="Secs" />
      </div>
    </div>
  );
};

// --- Component: Navbar ---
const Navbar = ({ onAuth }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 w-full z-50 px-6 py-5 flex justify-between items-center transition-all duration-500 ${scrolled ? 'bg-black/90 backdrop-blur-md shadow-lg border-b border-white/5' : 'bg-gradient-to-b from-black/90 to-transparent'}`}>
      <span className="font-title text-2xl md:text-4xl tracking-widest cursor-pointer hover:text-[#cda85f] transition-colors">
        FROM
      </span>
      <div className="flex items-center gap-6">
        <button className="hidden md:flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-white transition uppercase tracking-[0.2em] group">
          <Clock size={14} className="group-hover:text-[#cda85f] transition-colors" /> MGM+ Network
        </button>
        <button 
          onClick={onAuth} 
          aria-label="User Login"
          className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white backdrop-blur-md hover:bg-[#cda85f] hover:border-[#cda85f] hover:text-black transition-all duration-300"
        >
          <User size={18} />
        </button>
      </div>
    </nav>
  );
};

// --- Component: Hero Section ---
const Hero = ({ onWatch }) => {
  const [now, setNow] = useState(new Date().getTime());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date().getTime()), 1000);
    return () => clearInterval(interval);
  }, []);

  const isPremiereReleased = now >= RELEASE_CONFIG.premiere;
  const isFinaleReleased = now >= RELEASE_CONFIG.finale;

  let targetDate = RELEASE_CONFIG.premiere;
  let countdownTitle = "Season 4 Premiere Countdown";
  let heroText = <>Once you enter, you can never leave. Season 4 premieres <span className="font-semibold text-white">April 19, 2026</span>.</>;
  
  if (isPremiereReleased && !isFinaleReleased) {
    targetDate = RELEASE_CONFIG.finale;
    countdownTitle = "Season Finale Countdown";
    heroText = <>The nightmare continues weekly. <br/>The Season Finale arrives <span className="font-semibold text-[#cda85f]">June 21, 2026</span>.</>;
  } else if (isFinaleReleased) {
    targetDate = null;
    heroText = <>The harrowing conclusion is here.<br/><span className="text-[#cda85f] font-bold drop-shadow-[0_0_10px_rgba(205,168,95,0.8)]">ALL EPISODES STREAMING NOW</span></>;
  }

  const WatchButtonSet = ({ label, subLabel, primary, onWatch, icon: Icon = Play, glow = false }) => (
    <div className="flex flex-col md:flex-row items-stretch justify-center gap-5 w-full">
      <button 
        onClick={() => onWatch('1080p')}
        className={`group flex-1 relative overflow-hidden ${primary ? 'bg-[#cda85f] text-black hover:bg-[#e0bc75]' : 'bg-zinc-900/80 hover:bg-zinc-800 text-white'} py-4 px-6 rounded-lg transition-all transform hover:-translate-y-1 ${glow ? 'hover:shadow-[0_0_40px_rgba(205,168,95,0.4)] shadow-[0_0_20px_rgba(205,168,95,0.2)]' : ''} flex items-center justify-center gap-4 border border-transparent`}
      >
        <div className={`bg-black/10 p-2 rounded-full transition-colors z-10 ${primary ? 'group-hover:bg-white/20' : 'bg-white/10'}`}>
              <Icon size={24} className="fill-current pl-1" />
        </div>
        <div className="text-left z-10">
          <div className="text-sm font-bold uppercase tracking-wider">{label}</div>
          <div className="text-xs opacity-80 font-medium">{subLabel || '1080p FULL HD (Hindi-English)'}</div>
        </div>
        <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-20 group-hover:animate-shine" />
      </button>

      <button 
        onClick={() => onWatch('720p')}
        className="group flex-1 bg-black/40 hover:bg-black/60 backdrop-blur-md border border-white/10 hover:border-white/30 text-white py-4 px-6 rounded-lg transition-all transform hover:-translate-y-1 flex items-center justify-center gap-4"
      >
        <div className="bg-white/10 p-2 rounded-full">
            <Wifi size={24} />
        </div>
        <div className="text-left">
          <div className="text-sm font-bold uppercase tracking-wider">{label}</div>
          <div className="text-xs opacity-70 font-medium">720p HD (Hindi-English)</div>
        </div>
      </button>
    </div>
  );

  return (
    <header className="relative w-full min-h-screen flex items-center justify-center overflow-hidden pb-20 pt-32">
      {/* Background Layers */}
      <div className="absolute inset-0 z-0 select-none">
        <img 
          src="https://images.unsplash.com/photo-1503756234508-e32369269deb?q=80&w=2670&auto=format&fit=crop"
          alt="From Series Dark Road Background" 
          className="w-full h-full object-cover opacity-50 animate-slow-zoom"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-[#030405]"></div>
        <div className="absolute inset-0 bg-radial-gradient from-transparent via-transparent to-black/90"></div>
        <div className="absolute inset-0 bg-[#cda85f]/5 mix-blend-overlay"></div>
      </div>

      <DustParticles />

      {/* Hero Content */}
      <div className="relative z-30 text-center px-6 max-w-5xl mx-auto flex flex-col items-center animate-fade-up">
        
        <div className="mb-8 relative group">
           <h1 className="font-title text-7xl md:text-9xl mb-2 tracking-[0.2em] drop-shadow-2xl">FROM</h1>
           <p className="text-[#cda85f] uppercase tracking-[0.5em] font-bold text-sm md:text-lg">Season 4</p>
        </div>

        {targetDate && <CountdownDisplay targetDate={targetDate} title={countdownTitle} />}

        <p className="text-lg md:text-2xl text-gray-300 mb-10 font-light max-w-3xl mx-auto drop-shadow-md leading-relaxed">
          {heroText}
        </p>

        {/* Action Buttons Container */}
        <div className="w-full max-w-2xl space-y-8">
          
          {isFinaleReleased ? (
            <div className="relative transform scale-105">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#cda85f] px-4 py-1 rounded-sm text-[11px] text-black uppercase tracking-[0.2em] font-extrabold z-20 shadow-[0_0_20px_rgba(205,168,95,0.6)] animate-pulse">
                The Final Confrontation
              </div>
              <div className="p-1 rounded-xl bg-gradient-to-b from-[#cda85f]/30 to-transparent">
                  <WatchButtonSet 
                    label="Watch Season Finale" 
                    subLabel="S4 E10 • If a Tree Falls..."
                    primary={true} 
                    glow={true}
                    onWatch={onWatch} 
                    icon={TreePine}
                  />
              </div>
            </div>
          ) : (
            <>
              {isPremiereReleased && (
                <div className="relative mb-6">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#030405] px-2 text-[10px] text-[#cda85f] uppercase tracking-[0.2em] font-bold z-10">Latest Episodes</div>
                  <div className="pt-4 border-t border-white/5">
                      <WatchButtonSet label="EPISODE 5" primary={true} onWatch={onWatch} />
                  </div>
                </div>
              )}
              <div className="relative">
                 <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#030405] px-2 text-[10px] text-gray-500 uppercase tracking-[0.2em] font-bold z-10">Season 4</div>
                 <div className="pt-4 border-t border-white/5">
                     <WatchButtonSet label="Watch Premiere" primary={!isPremiereReleased} onWatch={onWatch} />
                 </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="absolute bottom-10 animate-bounce text-gray-600 z-30 opacity-50">
        <ChevronDown size={32} />
      </div>
    </header>
  );
};

// --- Component: Episodes Timeline ---
const Timeline = ({ episodes }) => {
  const [activeEp, setActiveEp] = useState(0);
  const [now, setNow] = useState(new Date().getTime());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date().getTime()), 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  const getEpisodeStatus = (epDateString) => {
    // Parse "April 19, 2026" to a timestamp
    const epTime = new Date(epDateString).getTime();
    return now >= epTime;
  };

  return (
    <section className="relative py-32 bg-[#030405]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-20">
          
          {/* Left: Info Dossier */}
          <div className="lg:w-1/3 lg:sticky lg:top-32 h-fit">
            <h2 className="font-display text-5xl md:text-6xl text-white mb-6 tracking-wide">Welcome to the Town</h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-10 font-light border-l-2 border-[#cda85f]/50 pl-6">
              Residents continue their desperate search for a way out, as new arrivals bring fresh chaos and ominous discoveries put Boyd's inner circle on high alert. The deeper they search, the darker it gets.
            </p>
            
            <div className="bg-white/5 border border-white/5 p-8 rounded-xl backdrop-blur-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-[#cda85f]"></div>
                <h4 className="text-white font-bold uppercase tracking-widest text-sm mb-6 flex items-center gap-2">
                    <Calendar size={16} className="text-[#cda85f]"/> Release Schedule
                </h4>
                <div className="space-y-5 text-sm">
                  {[
                      { label: "Premiere (E1)", date: "Apr 19, 2026", isOut: getEpisodeStatus("April 19, 2026") },
                      { label: "Mid-Season (E5)", date: "May 17, 2026", isOut: getEpisodeStatus("May 17, 2026") },
                      { label: "Season Finale (E10)", date: "Jun 21, 2026", isOut: getEpisodeStatus("June 21, 2026") }
                  ].map((item, i) => (
                    <div key={i} className={`flex justify-between items-center ${i !== 2 ? 'border-b border-white/5 pb-4' : ''}`}>
                      <span className={`${item.isOut ? 'text-white font-bold' : 'text-gray-500'}`}>{item.label}</span>
                      <div className="flex items-center gap-2">
                        {item.isOut ? (
                            <span className="text-[#cda85f] font-bold text-xs uppercase bg-[#cda85f]/10 px-2 py-1 rounded">Released</span>
                        ) : (
                            <span className="text-gray-600 flex items-center gap-1"><Clock size={12}/> {item.date}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
            </div>
          </div>

          {/* Right: Interactive Timeline */}
          <div className="lg:w-2/3 relative">
            <div className="absolute left-8 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-[#cda85f]/30 to-transparent hidden md:block"></div>

            <div className="space-y-6">
              {episodes.map((ep, idx) => {
                const isReleased = getEpisodeStatus(ep.date);
                const isFinalEp = idx === episodes.length - 1;
                
                return (
                    <div 
                      key={idx}
                      onMouseEnter={() => setActiveEp(idx)}
                      className={`group relative flex flex-col md:flex-row gap-6 p-6 rounded-2xl transition-all duration-500 border border-transparent cursor-default
                        ${activeEp === idx ? 'bg-white/5 border-white/10 shadow-2xl scale-[1.02]' : 'hover:bg-white/5 border-transparent opacity-60 hover:opacity-100'}
                        ${isFinalEp && isReleased ? 'border-[#cda85f]/30 bg-[#cda85f]/5' : ''}
                      `}
                    >
                      {/* Episode Number */}
                      <div className={`hidden md:flex flex-shrink-0 flex-col items-center justify-center w-16 h-16 rounded-full border-2 bg-[#030405] z-10 
                        ${activeEp === idx ? 'border-[#cda85f] text-[#cda85f] shadow-[0_0_15px_rgba(205,168,95,0.3)]' : 'border-gray-800 text-gray-600'} 
                        transition-all duration-300 font-display text-3xl`}
                      >
                        {idx + 1}
                      </div>

                      {/* Text Content */}
                      <div className="flex-1 z-10">
                        <div className="flex items-center gap-3 mb-2">
                           <div className="md:hidden text-[#cda85f] font-bold text-xs tracking-widest">EPISODE {idx + 1}</div>
                           {isFinalEp && <span className="text-[10px] font-bold bg-[#cda85f] text-black px-2 py-0.5 rounded uppercase tracking-wider">Season Finale</span>}
                        </div>
                        
                        <h3 className={`text-2xl font-bold mb-3 ${activeEp === idx ? 'text-white' : 'text-gray-400'} group-hover:text-white transition-colors`}>
                            {ep.title}
                        </h3>
                        <p className="text-gray-400 text-sm leading-relaxed mb-4">{ep.desc}</p>
                        
                        {/* Dynamic Availability Badge */}
                        {isReleased ? (
                            <div className="inline-flex items-center gap-2 text-xs font-bold text-green-500 uppercase tracking-widest animate-fade-up">
                                <CheckCircle size={14} /> Streaming Now
                            </div>
                        ) : (
                            <div className="inline-flex items-center gap-2 text-xs font-bold text-[#cda85f] uppercase tracking-widest">
                                <Monitor size={14} /> Airs {formatDate(ep.date)}
                            </div>
                        )}
                      </div>

                      {/* Image Preview */}
                      <div className={`hidden md:block w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden transition-all duration-700 transform border border-white/5
                        ${activeEp === idx ? 'opacity-100 translate-x-0 grayscale-0' : 'opacity-0 translate-x-10 grayscale'}
                      `}>
                        <img src={ep.img} alt={ep.title} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

// --- Component: Trailer Section ---
const Trailer = () => {
  const videos = [
    { id: 'eo0X8KVyP4o', title: 'Season 4 Official Trailer', label: 'Trailer' },
    { id: 'VGL7Wpdxdhg', title: 'Trailer | Critics Say', label: 'Critics Say | Trailer' },
    { id: 'BtD7p5f0KpI', title: 'Season 4 | Teaser', label: 'Teaser' }
  ];
  const [active, setActive] = useState(videos[0]);

  return (
    <section className="relative w-full py-24 bg-[#020202] overflow-hidden group border-t border-white/5">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]"></div>

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-4">
               <div>
                   <h2 className="text-4xl md:text-5xl font-display tracking-wider text-white mb-2">{active.title}</h2>
                   <p className="text-gray-500 text-sm uppercase tracking-widest">{active.label}</p>
               </div>
               <button className="text-white/60 hover:text-white transition flex items-center gap-2 text-sm uppercase font-bold tracking-widest border-b border-transparent hover:border-[#cda85f] pb-1">
                  View All Videos <Maximize2 size={16}/>
               </button>
          </div>

          <div className="relative aspect-video w-full rounded-xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.8)] border border-gray-800 group-hover:border-[#cda85f]/30 transition-colors duration-500 mb-8 bg-black">
              <div className="absolute -inset-1 bg-[#cda85f]/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              {/* Replace with actual MGM+ From trailers if known, these are placeholders using typical YT formats */}
              <div className="absolute inset-0 flex items-center justify-center text-gray-600 font-display text-2xl tracking-widest z-0">VIDEO UNAVAILABLE IN PREVIEW</div>
              <iframe
                  key={active.id}
                  className="absolute inset-0 w-full h-full z-10"
                  src={`https://www.youtube.com/embed/${active.id}?autoplay=0&rel=0`}
                  title={active.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
              ></iframe>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {videos.map((video) => (
              <div 
                key={video.id}
                onClick={() => setActive(video)}
                className={`relative aspect-video cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-300 group/item bg-black
                  ${active.id === video.id ? 'border-[#cda85f] opacity-100 scale-105' : 'border-transparent opacity-50 hover:opacity-100 hover:border-gray-600'}
                `}
              >
                <img 
                  src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`} 
                  alt={video.title}
                  className="w-full h-full object-cover mix-blend-luminosity"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover/item:opacity-100 transition-opacity">
                    <Play size={20} className="fill-[#cda85f] text-[#cda85f] drop-shadow-lg" />
                </div>
                <div className="absolute bottom-0 w-full bg-gradient-to-t from-black to-transparent p-2">
                  <span className="text-[10px] uppercase font-bold text-gray-300 tracking-wider">{video.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
    </section>
  );
};

// --- Component: Footer ---
const Footer = ({ onLogged }) => {
    return (
      <footer className="bg-[#020202] border-t border-white/5 pt-20 pb-12">
        <div className="max-w-5xl mx-auto px-6 flex flex-col items-center text-center">
             <div className="flex gap-10 mb-10">
                 {[Share2, Calendar, Info].map((Icon, i) => (
                    <div key={i} className="p-3 rounded-full bg-white/5 hover:bg-white/10 hover:text-[#cda85f] text-gray-400 cursor-pointer transition-all duration-300 hover:scale-110">
                        <Icon size={20} />
                    </div>
                 ))}
             </div>
             <p className="text-gray-600 text-sm mb-8 max-w-lg leading-relaxed">
                <span className="block mb-2 text-gray-400 font-semibold uppercase tracking-widest text-xs">Fan Made Portal</span>
                This is not an official MGM+ page. FROM is an MGM+ Original Series. Created for the fans, by the fans. Destination links direct to the requested episodes.
             </p>
             <div onClick={onLogged} className="flex gap-8 text-[10px] text-gray-500 font-bold uppercase tracking-[0.2em]">
                {['Help', 'Contact', 'Request'].map((item) => (
                    <span key={item} className="hover:text-white cursor-pointer transition-colors">{item}</span>
                ))}
             </div>
             <div className="mt-16 opacity-30 grayscale hover:grayscale-0 hover:opacity-100 transition duration-700 cursor-pointer">
                <span className="font-title text-3xl text-[#cda85f] tracking-[0.3em] drop-shadow-md">MGM+</span>
             </div>
        </div>
      </footer>
    );
};

// --- Main Application ---
const App = () => {
  const handleWatch = useCallback((type) => {
    fetch('https://api.notreal003.org/collect/linkclicked', { method: 'GET', keepalive: true })
      .catch(err => console.error("Analytics failed", err));
    const url = 'https://shrinkme.click/Omj51';
    window.open(url, '_blank');
  }, []);

  const handleAuth = useCallback(() => {
    window.open('https://api.notreal003.org/auth/google', '_blank');
  }, []);

  const episodesData = [
  {
    title: "The Arrival",
    desc: "A new arrival causes chaos in the town. Jade and Tabitha struggle with revelations found at the Bottle Tree, while Boyd deals with the implications of Smiley’s return.",
    date: "2026-04-19T01:00:00Z",
    img: "https://images.unsplash.com/photo-1518495973542-4542c06a5843?q=80&w=2670&auto=format&fit=crop"
  },
  {
    title: "Fray",
    desc: "A gruesome discovery sends shockwaves through the community.",
    date: "2026-04-26T01:00:00Z",
    img: "https://images.unsplash.com/photo-1509347528160-9a9e33742cd4?q=80&w=2670&auto=format&fit=crop"
  },
  {
    title: "Merrily We Go",
    desc: "Boyd attempts to save Acosta from herself. Julie begins to explore her newfound abilities, and Victor joins Ethan on a quest for answers.",
    date: "2026-05-03T01:00:00Z",
    img: "https://images.unsplash.com/photo-1476231682828-37e571bc172f?q=80&w=2670&auto=format&fit=crop"
  },
  {
    title: "Of Myths and Monsters",
    desc: "An ominous discovery puts Boyd’s inner circle on high alert. Meanwhile, Sara is once again tortured by mysterious voices.",
    date: "2026-05-10T01:00:00Z",
    img: "https://images.unsplash.com/photo-1519330377309-9b2fdf92afb8?q=80&w=2670&auto=format&fit=crop"
  },
  {
    title: "What a Long Strange Trip It's Been",
    desc: "Boyd and Jade embark on a mind-bending search for answers.",
    date: "2026-05-17T01:00:00Z",
    img: "https://images.unsplash.com/photo-1439241416685-64359404279b?q=80&w=2670&auto=format&fit=crop"
  },
  {
    title: "The Heart Is a Lonely Hunter",
    desc: "A rift forms between Boyd and Jade over whether to trust Jade's visions, as troubling news arrives from the settlement.",
    date: "2026-05-24T01:00:00Z",
    img: "https://images.unsplash.com/photo-1513836279014-a89f7a76ae86?q=80&w=2670&auto=format&fit=crop"
  },
  {
    title: "Best Laid Plans",
    desc: "Tabitha and Jade’s troubling history is brought to light, while Boyd prepares to test a dangerous theory.",
    date: "2026-05-31T01:00:00Z",
    img: "https://images.unsplash.com/photo-1626814026160-2237a95fc5a0?q=80&w=2670&auto=format&fit=crop"
  },
  {
    title: "Heavy Is the Head",
    desc: "Boyd weighs the risks of a plan to get everyone home. Fatima and Henry face disturbing crossroads.",
    date: "2026-06-07T01:00:00Z",
    img: "https://images.unsplash.com/photo-1594909122845-11baa439b7bf?q=80&w=2670&auto=format&fit=crop"
  },
  {
    title: "The Calm Before",
    desc: "Residents continue their search for a way out as the season nears its climax.",
    date: "2026-06-14T01:00:00Z",
    img: "https://images.unsplash.com/photo-1605806616949-1e87b487bc2a?q=80&w=2670&auto=format&fit=crop"
  },
  {
    title: "If a Tree Falls in the Forest…",
    desc: "The season finale focuses on final confrontations and potential escapes.",
    date: "2026-06-21T01:00:00Z",
    img: "https://images.unsplash.com/photo-1533613220915-609f661a6fe1?q=80&w=2670&auto=format&fit=crop"
  }
];

  return (
    <main className="min-h-screen relative text-white selection:bg-[#cda85f] selection:text-black">
      <GlobalStyles />
      <div className="film-grain"></div>
      <Navbar onAuth={handleAuth} />
      <Hero onWatch={handleWatch} />
      <Timeline episodes={episodesData} />
      <Trailer />
      <Footer onLogged={handleAuth} />
    </main>
  );
};

export default App;
